import { API, BACKEND_URL } from '@/config/api';
import { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/App';
import { isDemoUser } from '@/lib/utils';
import AdminNavigation from '@/components/AdminNavigation';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter 
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { 
  Search, UserPlus, Building2, Users, Trash2, Edit2 as Edit, Mail, Phone, MapPin, Lock, Shield,
  Loader2, ChevronDown, ChevronRight, Plus, X, Check
} from 'lucide-react';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger
} from '@/components/ui/alert-dialog';

// Import refactored components
import { AddUserDialog, AddCompanyDialog, CompanyCard, UserManagementHeader } from './AdminUserManagement';

export default function AdminUserManagement() {
  const { user } = useAuth();
  const isDemo = isDemoUser(user);
  const [companies, setCompanies] = useState([]);
  const [users, setUsers] = useState([]);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [companyDialogOpen, setCompanyDialogOpen] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState('all');
  const [editingCompany, setEditingCompany] = useState(null);
  const [newUserData, setNewUserData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'customer',
    company_id: '',
    auth_provider: 'local', // 'local' or 'microsoft'
    skip_2fa: false,
    phone_number: '',
    phone_country_code: '+47'
  });
  const [newCompanyData, setNewCompanyData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    areas: []
  });
  const [editCompanyData, setEditCompanyData] = useState({
    name: '',
    contact_person: '',
    email: '',
    phone: '',
    areas: []
  });
  
  // User editing states
  const [editUserDialogOpen, setEditUserDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [editUserData, setEditUserData] = useState({
    name: '',
    email: '',
    role: 'admin',
    auth_provider: 'local',
    new_password: '',
    is_active: true,
    skip_2fa: false,
    phone_number: '',
    phone_country_code: '+47',
    sms_2fa_enabled: false,
    sms_notifications_enabled: false,
    company_id: ''
  });
  const [expandedAdmins, setExpandedAdmins] = useState(false);
  const [expandedCompanies, setExpandedCompanies] = useState(new Set());

  // Get admin users
  const adminUsers = users.filter(u => u.role === 'admin');
  const customerUsers = users.filter(u => u.role === 'customer');

  // Open edit dialog for a user
  const openEditUserDialog = (userToEdit) => {
    setEditingUser(userToEdit);
    // Parse phone: if starts with +XX, split into country code + number
    let phoneCode = '+47';
    let phoneNum = '';
    if (userToEdit.phone_number) {
      const match = userToEdit.phone_number.match(/^(\+\d{1,3})(.*)$/);
      if (match) {
        phoneCode = match[1];
        phoneNum = match[2].trim();
      } else {
        phoneNum = userToEdit.phone_number;
      }
    }
    setEditUserData({
      name: userToEdit.name || '',
      email: userToEdit.email || '',
      role: userToEdit.role || 'admin',
      auth_provider: userToEdit.auth_provider || 'local',
      new_password: '',
      is_active: userToEdit.is_active !== false,
      skip_2fa: userToEdit.skip_2fa || false,
      phone_number: phoneNum,
      phone_country_code: phoneCode,
      sms_2fa_enabled: userToEdit.sms_2fa_enabled || false,
      sms_notifications_enabled: userToEdit.sms_notifications_enabled || false,
      company_id: userToEdit.company_id || ''
    });
    setEditUserDialogOpen(true);
  };

  // Save user edits
  const saveUserEdit = async () => {
    if (!editingUser) return;
    
    if (isDemo) {
      setUsers(prev => prev.map(u => 
        u.id === editingUser.id 
          ? { ...u, ...editUserData, auth_provider: editUserData.auth_provider }
          : u
      ));
      setEditUserDialogOpen(false);
      setEditingUser(null);
      toast.success('Bruker oppdatert');
      return;
    }
    
    try {
      const updateData = {
        name: editUserData.name,
        email: editUserData.email,
        role: editUserData.role,
        auth_provider: editUserData.auth_provider,
        is_active: editUserData.is_active,
        skip_2fa: editUserData.skip_2fa,
        sms_2fa_enabled: editUserData.sms_2fa_enabled,
        sms_notifications_enabled: editUserData.sms_notifications_enabled
      };
      
      // Include phone if set
      if (editUserData.phone_number) {
        const cleanNum = editUserData.phone_number.replace(/[\s-]/g, '');
        updateData.phone_number = editUserData.phone_country_code + cleanNum;
      } else {
        updateData.phone_number = '';
      }

      // Include company_id for customer users
      if (editUserData.role === 'customer' && editUserData.company_id) {
        updateData.company_id = editUserData.company_id;
      }
      
      // Only include password if it's set
      if (editUserData.new_password) {
        updateData.password = editUserData.new_password;
      }
      
      await axios.put(`${API}/users/${editingUser.id}`, updateData, { withCredentials: true });
      await fetchData();
      setEditUserDialogOpen(false);
      setEditingUser(null);
      toast.success('Bruker oppdatert');
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke oppdatere bruker');
    }
  };

  const fetchData = useCallback(async () => {
    if (isDemo) {
      // Demo areas for demonstration purposes
      setAreas([
        { id: 'p02-rogaland', name: 'P02 Rogaland', region: 'Rogaland' },
        { id: 'p03-rogaland', name: 'P03 Rogaland', region: 'Rogaland' },
        { id: 'p04-hordaland', name: 'P04 Hordaland', region: 'Hordaland' },
      ]);
      
      // Demo companies for demonstration purposes
      setCompanies([
        { id: 'company-mowi', name: 'Mowi ASA', contact_person: 'Lars Hansen', email: 'lars@mowi.no', areas: ['p02-rogaland', 'p04-hordaland'] },
        { id: 'company-leroy', name: 'Lerøy Seafood', contact_person: 'Kari Berg', email: 'kari@leroy.no', areas: ['p03-rogaland'] },
        { id: 'company-salmar', name: 'SalMar ASA', contact_person: 'Erik Olsen', email: 'erik@salmar.no', areas: [] }
      ]);
      
      // Demo users with company relationships for demonstration purposes
      setUsers([
        { id: 'user-1', name: 'Lars Hansen', email: 'lars@mowi.no', role: 'customer', company_id: 'company-mowi', company_name: 'Mowi ASA', created_at: '2025-01-15' },
        { id: 'user-2', name: 'Anne Larsen', email: 'anne@mowi.no', role: 'customer', company_id: 'company-mowi', company_name: 'Mowi ASA', created_at: '2025-01-20' },
        { id: 'user-3', name: 'Kari Berg', email: 'kari@leroy.no', role: 'customer', company_id: 'company-leroy', company_name: 'Lerøy Seafood', created_at: '2025-01-18' },
        { id: 'user-4', name: 'Ole Nilsen', email: 'ole@leroy.no', role: 'customer', company_id: 'company-leroy', company_name: 'Lerøy Seafood', created_at: '2025-01-22' },
        { id: 'user-5', name: 'Erik Olsen', email: 'erik@salmar.no', role: 'customer', company_id: 'company-salmar', company_name: 'SalMar ASA', created_at: '2025-01-19' }
      ]);
      
      setLoading(false);
      return;
    }
    
    try {
      const [companiesRes, usersRes, areasRes] = await Promise.all([
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/users`, { withCredentials: true }),
        axios.get(`${API}/areas`, { withCredentials: true })
      ]);
      setCompanies(companiesRes.data);
      setUsers(usersRes.data);
      setAreas(areasRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching data:', error);
      setLoading(false);
    }
  }, [isDemo]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const addUser = async () => {
    // For Microsoft users, password is not required
    const isMicrosoftUser = newUserData.auth_provider === 'microsoft';
    const isAdmin = newUserData.role === 'admin';
    
    // Validation - admin users don't need a company
    if (!newUserData.name || !newUserData.email) {
      toast.error('Fyll ut navn og e-post');
      return;
    }
    
    // Company is only required for customer users
    if (!isAdmin && !newUserData.company_id) {
      toast.error('Velg et selskap for kundebrukere');
      return;
    }
    
    // Password is only required for local users
    if (!isMicrosoftUser && !newUserData.password) {
      toast.error('Passord er påkrevd for lokale brukere');
      return;
    }

    if (isDemo) {
      const company = companies.find(c => c.id === newUserData.company_id);
      const newUser = {
        id: `user-${Date.now()}`,
        name: newUserData.name,
        email: newUserData.email,
        role: newUserData.role,
        company_id: isAdmin ? null : newUserData.company_id,
        company_name: isAdmin ? null : company?.name,
        auth_provider: newUserData.auth_provider,
        skip_2fa: newUserData.skip_2fa,
        created_at: new Date().toISOString().split('T')[0]
      };
      
      setUsers(prev => [...prev, newUser]);
      setUserDialogOpen(false);
      setNewUserData({ name: '', email: '', password: '', role: 'customer', company_id: '', auth_provider: 'local', skip_2fa: false, phone_number: '', phone_country_code: '+47' });
      toast.success('Bruker lagt til');
      return;
    }
    
    try {
      // Create user data object, omitting password for Microsoft users
      const userData = {
        name: newUserData.name,
        email: newUserData.email,
        role: newUserData.role,
        company_id: isAdmin ? null : newUserData.company_id,
        auth_provider: newUserData.auth_provider,
        skip_2fa: newUserData.skip_2fa
      };
      
      if (!isMicrosoftUser) {
        userData.password = newUserData.password;
      }
      
      // Add phone number if provided (for customer users)
      if (newUserData.phone_number && !isAdmin) {
        userData.phone_number = newUserData.phone_country_code + newUserData.phone_number.replace(/\s/g, '');
        userData.sms_notifications_enabled = true;
      }
      
      await axios.post(`${API}/users`, userData, { withCredentials: true });
      toast.success(isMicrosoftUser ? 'Microsoft-bruker opprettet' : 'Bruker opprettet');
      setUserDialogOpen(false);
      setNewUserData({ name: '', email: '', password: '', role: 'customer', company_id: '', auth_provider: 'local', skip_2fa: false, phone_number: '', phone_country_code: '+47' });
      fetchData();
    } catch (error) {
      console.error('Error creating user:', error);
      toast.error(error.response?.data?.detail || 'Kunne ikke opprette bruker');
    }
  };

  const addCompany = async () => {
    if (!newCompanyData.name) {
      toast.error('Selskapsnavn er påkrevd');
      return;
    }

    if (isDemo) {
      const newCompany = {
        id: `company-${Date.now()}`,
        ...newCompanyData,
        created_at: new Date().toISOString().split('T')[0]
      };
      
      setCompanies(prev => [...prev, newCompany]);
      setCompanyDialogOpen(false);
      setNewCompanyData({ name: '', contact_person: '', email: '', phone: '', areas: [] });
      toast.success('Kunde opprettet');
      return;
    }
    
    try {
      await axios.post(`${API}/companies`, newCompanyData, { withCredentials: true });
      toast.success('Kunde opprettet');
      setCompanyDialogOpen(false);
      setNewCompanyData({ name: '', contact_person: '', email: '', phone: '', areas: [] });
      fetchData();
    } catch (error) {
      console.error('Error creating company:', error);
      toast.error('Kunne ikke opprette kunde');
    }
  };
  
  const toggleArea = (areaId) => {
    setNewCompanyData(prev => ({
      ...prev,
      areas: prev.areas.includes(areaId)
        ? prev.areas.filter(id => id !== areaId)
        : [...prev.areas, areaId]
    }));
  };

  const toggleEditArea = (areaId) => {
    setEditCompanyData(prev => ({
      ...prev,
      areas: prev.areas.includes(areaId)
        ? prev.areas.filter(id => id !== areaId)
        : [...prev.areas, areaId]
    }));
  };

  const openEditDialog = (company) => {
    setEditingCompany(company);
    setEditCompanyData({
      name: company.name || '',
      contact_person: company.contact_person || '',
      email: company.email || '',
      phone: company.phone || '',
      areas: company.areas || []
    });
    setEditCompanyDialogOpen(true);
  };

  const updateCompany = async () => {
    if (!editCompanyData.name) {
      toast.error('Selskapsnavn er påkrevd');
      return;
    }

    if (isDemo) {
      setCompanies(prev => prev.map(c => 
        c.id === editingCompany.id 
          ? { ...c, ...editCompanyData }
          : c
      ));
      setEditCompanyDialogOpen(false);
      setEditingCompany(null);
      toast.success('Kunde oppdatert');
      return;
    }
    
    try {
      await axios.put(`${API}/companies/${editingCompany.id}`, editCompanyData, { withCredentials: true });
      toast.success('Kunde oppdatert');
      setEditCompanyDialogOpen(false);
      setEditingCompany(null);
      fetchData();
    } catch (error) {
      console.error('Error updating company:', error);
      toast.error('Kunne ikke oppdatere kunde');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Er du sikker på at du vil slette denne brukeren?')) {
      return;
    }

    if (isDemo) {
      setUsers(prev => prev.filter(user => user.id !== userId));
      toast.success('Bruker slettet');
      return;
    }
    
    try {
      await axios.delete(`${API}/users/${userId}`, { withCredentials: true });
      toast.success('Bruker slettet');
      fetchData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Kunne ikke slette bruker');
    }
  };

  const getUsersByCompany = (companyId) => {
    return users.filter(user => user.company_id === companyId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-teal-50/30 flex">
        <AdminNavigation />
        <main className="flex-1 flex items-center justify-center p-4 lg:p-8">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-teal-600 mx-auto mb-4" />
            <p className="text-slate-600">Laster brukerdata...</p>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex">
      <AdminNavigation />
      <main className="flex-1 p-4 lg:p-8 overflow-x-hidden">
        <div className="max-w-5xl mx-auto">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Kunder</h1>
              <p className="text-sm text-slate-500 mt-1">Administrer kundar, brukarar og tilgangar</p>
            </div>
            
            <div className="flex gap-2">
              <Dialog open={companyDialogOpen} onOpenChange={setCompanyDialogOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-500 hover:bg-teal-600 shadow-sm shadow-teal-500/20" size="sm">
                    <Building2 className="w-4 h-4 mr-1.5" />
                    Ny kunde
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Legg til ny kunde</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Selskapsnavn *</Label>
                      <Input
                        value={newCompanyData.name}
                        onChange={(e) => setNewCompanyData({ ...newCompanyData, name: e.target.value })}
                        placeholder="Eksempel AS"
                      />
                    </div>
                    <div>
                      <Label>Kontaktperson</Label>
                      <Input
                        value={newCompanyData.contact_person}
                        onChange={(e) => setNewCompanyData({ ...newCompanyData, contact_person: e.target.value })}
                        placeholder="Fornavn Etternavn"
                      />
                    </div>
                    <div>
                      <Label>E-post</Label>
                      <Input
                        type="email"
                        value={newCompanyData.email}
                        onChange={(e) => setNewCompanyData({ ...newCompanyData, email: e.target.value })}
                        placeholder="kontakt@selskap.no"
                      />
                    </div>
                    <div>
                      <Label>Telefon</Label>
                      <Input
                        value={newCompanyData.phone}
                        onChange={(e) => setNewCompanyData({ ...newCompanyData, phone: e.target.value })}
                        placeholder="+47 123 45 678"
                      />
                    </div>
                    <div>
                      <Label className="mb-3 block">Tilknyttede områder</Label>
                      <div className="space-y-2 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                        {areas.map((area) => (
                          <div key={area.id} className="flex items-center space-x-2">
                            <Checkbox
                              id={`area-${area.id}`}
                              checked={newCompanyData.areas.includes(area.id)}
                              onCheckedChange={() => toggleArea(area.id)}
                            />
                            <label
                              htmlFor={`area-${area.id}`}
                              className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                            >
                              {area.name}
                            </label>
                          </div>
                        ))}
                        {areas.length === 0 && (
                          <p className="text-sm text-slate-500">Ingen områder tilgjengelig</p>
                        )}
                      </div>
                      <p className="text-xs text-slate-500 mt-1">
                        {newCompanyData.areas.length} område(r) valgt
                      </p>
                    </div>
                    <div className="flex space-x-2 pt-4">
                      <Button onClick={addCompany} className="flex-1 bg-blue-600 hover:bg-blue-700">
                        Opprett kunde
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCompanyDialogOpen(false)}
                        className="flex-1"
                      >
                        Avbryt
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Dialog open={userDialogOpen} onOpenChange={setUserDialogOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" size="sm">
                    <UserPlus className="w-4 h-4 mr-1.5" />
                    Ny bruker
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Legg til ny bruker</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4">
                    {/* Auth Provider Selection */}
                    <div>
                      <Label>Innloggingsmetode</Label>
                      <Select 
                        value={newUserData.auth_provider} 
                        onValueChange={(value) => setNewUserData({ ...newUserData, auth_provider: value, password: value === 'microsoft' ? '' : newUserData.password })}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="local">
                            <div className="flex items-center gap-2">
                              <Lock className="w-4 h-4" />
                              <span>E-post og passord</span>
                            </div>
                          </SelectItem>
                          <SelectItem value="microsoft">
                            <div className="flex items-center gap-2">
                              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 21 21">
                                <rect x="1" y="1" width="9" height="9" fill="#f25022"/>
                                <rect x="11" y="1" width="9" height="9" fill="#7fba00"/>
                                <rect x="1" y="11" width="9" height="9" fill="#00a4ef"/>
                                <rect x="11" y="11" width="9" height="9" fill="#ffb900"/>
                              </svg>
                              <span>Microsoft-konto</span>
                            </div>
                          </SelectItem>
                        </SelectContent>
                      </Select>
                      {newUserData.auth_provider === 'microsoft' && (
                        <p className="text-xs text-blue-600 mt-1 flex items-center gap-1">
                          <Shield className="w-3 h-3" />
                          Microsoft-brukere trenger ikke 2FA - autentisering håndteres av Microsoft
                        </p>
                      )}
                    </div>
                    <div>
                      <Label>Navn *</Label>
                      <Input
                        value={newUserData.name}
                        onChange={(e) => setNewUserData({ ...newUserData, name: e.target.value })}
                        placeholder="Fornavn Etternavn"
                      />
                    </div>
                    <div>
                      <Label>E-post *</Label>
                      <Input
                        type="email"
                        value={newUserData.email}
                        onChange={(e) => setNewUserData({ ...newUserData, email: e.target.value })}
                        placeholder={newUserData.auth_provider === 'microsoft' ? 'bruker@organisasjon.onmicrosoft.com' : 'bruker@selskap.no'}
                      />
                      {newUserData.auth_provider === 'microsoft' && (
                        <p className="text-xs text-slate-500 mt-1">Bruk samme e-postadresse som i Microsoft-kontoen</p>
                      )}
                    </div>
                    {newUserData.auth_provider === 'local' && (
                      <div>
                        <Label>Passord *</Label>
                        <Input
                          type="password"
                          value={newUserData.password}
                          onChange={(e) => setNewUserData({ ...newUserData, password: e.target.value })}
                          placeholder="Minst 8 tegn"
                        />
                        <p className="text-xs text-slate-500 mt-1">Brukeren vil bruke dette passordet for å logge inn</p>
                      </div>
                    )}
                    <div>
                      <Label>Rolle</Label>
                      <div className="h-10 flex items-center px-3 bg-slate-50 border border-slate-200 rounded-md text-sm text-slate-700">
                        Kunde
                      </div>
                    </div>
                    {(
                      <div>
                        <Label>Selskap *</Label>
                        <Select 
                          value={newUserData.company_id} 
                          onValueChange={(value) => setNewUserData({ ...newUserData, company_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Velg selskap" />
                          </SelectTrigger>
                          <SelectContent>
                            {companies.map((company) => (
                              <SelectItem key={company.id} value={company.id}>
                                {company.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}
                    {(
                      <div>
                        <Label>Mobilnummer (for SMS-varsling)</Label>
                        <div className="flex gap-2">
                          <Select 
                            value={newUserData.phone_country_code} 
                            onValueChange={(value) => setNewUserData({ ...newUserData, phone_country_code: value })}
                          >
                            <SelectTrigger className="w-[100px]">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="+47">🇳🇴 +47</SelectItem>
                              <SelectItem value="+46">🇸🇪 +46</SelectItem>
                              <SelectItem value="+45">🇩🇰 +45</SelectItem>
                              <SelectItem value="+358">🇫🇮 +358</SelectItem>
                              <SelectItem value="+354">🇮🇸 +354</SelectItem>
                              <SelectItem value="+44">🇬🇧 +44</SelectItem>
                              <SelectItem value="+1">🇺🇸 +1</SelectItem>
                            </SelectContent>
                          </Select>
                          <Input
                            type="tel"
                            value={newUserData.phone_number}
                            onChange={(e) => setNewUserData({ ...newUserData, phone_number: e.target.value.replace(/[^0-9\s]/g, '') })}
                            placeholder="xxx xx xxx"
                            className="flex-1"
                          />
                        </div>
                        <p className="text-xs text-slate-500 mt-1">
                          Bruker vil automatisk motta SMS når nye prøver er klare
                        </p>
                      </div>
                    )}
                    {newUserData.auth_provider === 'local' && (
                      <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                        <Checkbox
                          id="new_skip_2fa"
                          checked={newUserData.skip_2fa}
                          onCheckedChange={(checked) => setNewUserData({ ...newUserData, skip_2fa: checked })}
                        />
                        <div>
                          <label htmlFor="new_skip_2fa" className="text-sm font-medium cursor-pointer text-amber-900">
                            Hopp over 2FA-krav
                          </label>
                          <p className="text-xs text-amber-700">Bruker trenger ikke sette opp to-faktor autentisering</p>
                        </div>
                      </div>
                    )}
                    <div className="flex space-x-2 pt-4">
                      <Button onClick={addUser} className="flex-1 bg-teal-600 hover:bg-teal-700">
                        Opprett bruker
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setUserDialogOpen(false)}
                        className="flex-1"
                      >
                        Avbryt
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </div>

          {/* Edit Company Dialog */}
          <Dialog open={editCompanyDialogOpen} onOpenChange={setEditCompanyDialogOpen}>
            <DialogContent className="max-w-xl">
              <DialogHeader>
                <DialogTitle>Rediger kunde</DialogTitle>
              </DialogHeader>
              <div className="space-y-4">
                <div>
                  <Label>Selskapsnavn *</Label>
                  <Input
                    value={editCompanyData.name}
                    onChange={(e) => setEditCompanyData({ ...editCompanyData, name: e.target.value })}
                    placeholder="Eksempel AS"
                  />
                </div>
                <div>
                  <Label>Kontaktperson</Label>
                  <Input
                    value={editCompanyData.contact_person}
                    onChange={(e) => setEditCompanyData({ ...editCompanyData, contact_person: e.target.value })}
                    placeholder="Fornavn Etternavn"
                  />
                </div>
                <div>
                  <Label>E-post</Label>
                  <Input
                    type="email"
                    value={editCompanyData.email}
                    onChange={(e) => setEditCompanyData({ ...editCompanyData, email: e.target.value })}
                    placeholder="kontakt@selskap.no"
                  />
                </div>
                <div>
                  <Label>Telefon</Label>
                  <Input
                    value={editCompanyData.phone}
                    onChange={(e) => setEditCompanyData({ ...editCompanyData, phone: e.target.value })}
                    placeholder="+47 123 45 678"
                  />
                </div>
                <div>
                  <Label className="mb-3 block">Tilknyttede områder</Label>
                  <div className="space-y-2 border border-slate-200 rounded-lg p-3 max-h-48 overflow-y-auto">
                    {areas.map((area) => (
                      <div key={area.id} className="flex items-center space-x-2">
                        <Checkbox
                          id={`edit-area-${area.id}`}
                          checked={editCompanyData.areas.includes(area.id)}
                          onCheckedChange={() => toggleEditArea(area.id)}
                        />
                        <label
                          htmlFor={`edit-area-${area.id}`}
                          className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                        >
                          {area.name}
                        </label>
                      </div>
                    ))}
                    {areas.length === 0 && (
                      <p className="text-sm text-slate-500">Ingen områder tilgjengelig</p>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 mt-1">
                    {editCompanyData.areas.length} område(r) valgt
                  </p>
                </div>
                <div className="flex space-x-2 pt-4">
                  <Button onClick={updateCompany} className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Lagre endringer
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setEditCompanyDialogOpen(false)}
                    className="flex-1"
                  >
                    Avbryt
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Filter */}
          <div className="mb-6 flex items-center gap-3">
            <Label className="text-xs font-medium text-slate-500 whitespace-nowrap">Filter:</Label>
            <Select value={selectedCompany} onValueChange={setSelectedCompany}>
              <SelectTrigger className="w-64 h-9 text-sm">
                <SelectValue placeholder="Alle selskaper" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Alle selskaper</SelectItem>
                {companies.map((company) => (
                  <SelectItem key={company.id} value={company.id}>
                    {company.name} ({getUsersByCompany(company.id).length})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Companies with Users */}
          <div className="space-y-4">
            {companies
              .filter(company => !selectedCompany || selectedCompany === "all" || company.id === selectedCompany)
              .map((company) => {
                const companyUsers = getUsersByCompany(company.id);
                
                return (
                  <Card key={company.id} className="rounded-2xl border border-slate-200 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`company-card-${company.id}`}>
                    <button
                      className="w-full text-left"
                      onClick={() => setExpandedCompanies(prev => {
                        const next = new Set(prev);
                        next.has(company.id) ? next.delete(company.id) : next.add(company.id);
                        return next;
                      })}
                    >
                    <CardHeader className="pb-3 bg-white border-b border-slate-100">
                      <div className="flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3 min-w-0">
                          <div className="w-10 h-10 bg-teal-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Building2 className="w-5 h-5 text-teal-600" />
                          </div>
                          <div className="min-w-0">
                            <h3 className="font-semibold text-base text-slate-900 truncate">{company.name}</h3>
                            <p className="text-xs text-slate-400">
                              {companyUsers.length} bruker{companyUsers.length !== 1 ? 'e' : ''}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {company.areas && company.areas.length > 0 && (
                            <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                              {company.areas.slice(0, 3).map(areaId => {
                                const area = areas.find(a => a.id === areaId);
                                return area ? (
                                  <span key={areaId} className="inline-flex items-center gap-1 text-[10px] font-medium text-teal-700 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
                                    <MapPin className="w-3 h-3" />{area.name}
                                  </span>
                                ) : null;
                              })}
                              {company.areas.length > 3 && (
                                <span className="text-[10px] text-slate-400">+{company.areas.length - 3}</span>
                              )}
                            </div>
                          )}
                          <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${expandedCompanies.has(company.id) ? '' : '-rotate-90'}`} />
                        </div>
                      </div>
                    </CardHeader>
                    </button>
                    {expandedCompanies.has(company.id) && (
                    <CardContent className="p-3 sm:p-4">
                      {companyUsers.length === 0 ? (
                        <div className="text-center py-6 text-slate-500">
                          <Users className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                          <p className="text-sm">Ingen brukere registrert</p>
                        </div>
                      ) : (
                        <div className="space-y-1">
                          {companyUsers.map((companyUser) => (
                            <div key={companyUser.id} className="flex items-center justify-between p-3 rounded-xl hover:bg-slate-50 transition-colors group" data-testid={`user-row-${companyUser.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-9 h-9 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="font-semibold text-teal-700 text-xs">
                                    {companyUser.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                                  </span>
                                </div>
                                <div className="min-w-0">
                                  <div className="font-medium text-slate-900 text-sm truncate">{companyUser.name}</div>
                                  <div className="text-xs text-slate-400 truncate">{companyUser.email}</div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2">
                                {companyUser.phone_number && (
                                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-teal-600 bg-teal-50 px-2 py-0.5 rounded-full border border-teal-100">
                                    <Phone className="w-3 h-3" />SMS
                                  </span>
                                )}
                                {companyUser.sms_2fa_enabled && (
                                  <span className="hidden sm:inline-flex items-center gap-1 text-[10px] text-sky-600 bg-sky-50 px-2 py-0.5 rounded-full border border-sky-100">
                                    <Shield className="w-3 h-3" />OTP
                                  </span>
                                )}
                                {companyUser.skip_2fa && (
                                  <span className="hidden sm:inline-flex items-center text-[10px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                                    Uten 2FA
                                  </span>
                                )}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openEditUserDialog(companyUser)}
                                  className="opacity-0 group-hover:opacity-100 transition-opacity h-8 px-2.5 text-slate-500 hover:text-teal-600 hover:bg-teal-50"
                                  data-testid={`edit-user-${companyUser.id}`}
                                >
                                  <Edit className="w-3.5 h-3.5 mr-1" />
                                  <span className="text-xs">Rediger</span>
                                </Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      className="opacity-0 group-hover:opacity-100 transition-opacity text-slate-400 hover:text-red-500 hover:bg-red-50 h-8 w-8 p-0"
                                    >
                                      <Trash2 className="w-3.5 h-3.5" />
                                    </Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader>
                                      <AlertDialogTitle>Slett bruker?</AlertDialogTitle>
                                      <AlertDialogDescription>
                                        Er du sikker på at du vil slette {companyUser.name}? Denne handlingen kan ikke angres.
                                      </AlertDialogDescription>
                                    </AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Avbryt</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => deleteUser(companyUser.id)} className="bg-red-600 hover:bg-red-700">Slett</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      <div className="mt-3 pt-3 border-t border-slate-100 flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setNewUserData({ ...newUserData, company_id: company.id, role: 'customer' });
                            setUserDialogOpen(true);
                          }}
                          className="text-teal-600 hover:text-teal-700 hover:bg-teal-50 h-8 text-xs"
                        >
                          <Plus className="w-3.5 h-3.5 mr-1" />
                          Legg til bruker
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditDialog(company)}
                          className="text-slate-500 hover:text-slate-700 hover:bg-slate-50 h-8 text-xs"
                        >
                          <Edit className="w-3.5 h-3.5 mr-1" />
                          Rediger kunde
                        </Button>
                      </div>
                    </CardContent>
                    )}
                  </Card>
                );
              })}
          </div>

          {/* Edit User Dialog */}
          <Dialog open={editUserDialogOpen} onOpenChange={setEditUserDialogOpen}>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Edit className="w-4 h-4 text-teal-500" />
                  Rediger {editingUser?.role === 'admin' ? 'administrator' : 'bruker'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
                {/* Basic info */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Navn</Label>
                    <Input
                      value={editUserData.name}
                      onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                      placeholder="Fullt navn"
                      className="h-10"
                    />
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">E-post</Label>
                    <Input
                      type="email"
                      value={editUserData.email}
                      onChange={(e) => setEditUserData({ ...editUserData, email: e.target.value })}
                      placeholder="bruker@example.com"
                      className="h-10"
                    />
                  </div>
                </div>

                {/* Role + Company */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Rolle</Label>
                    <Select 
                      value={editUserData.role} 
                      onValueChange={(value) => setEditUserData({ ...editUserData, role: value })}
                    >
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="admin">Administrator</SelectItem>
                        <SelectItem value="customer">Kunde</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Innlogging</Label>
                    <Select 
                      value={editUserData.auth_provider} 
                      onValueChange={(value) => setEditUserData({ 
                        ...editUserData, 
                        auth_provider: value,
                        new_password: value === 'microsoft' ? '' : editUserData.new_password 
                      })}
                    >
                      <SelectTrigger className="h-10"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="local">E-post/passord</SelectItem>
                        <SelectItem value="microsoft">Microsoft</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Company selector for customer role */}
                {editUserData.role === 'customer' && (
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Selskap</Label>
                    <Select value={editUserData.company_id} onValueChange={(v) => setEditUserData({ ...editUserData, company_id: v })}>
                      <SelectTrigger className="h-10"><SelectValue placeholder="Velg selskap" /></SelectTrigger>
                      <SelectContent>
                        {companies.map(c => (
                          <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Password */}
                {editUserData.auth_provider === 'local' && (
                  <div>
                    <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Nytt passord (valgfritt)</Label>
                    <Input
                      type="password"
                      value={editUserData.new_password}
                      onChange={(e) => setEditUserData({ ...editUserData, new_password: e.target.value })}
                      placeholder="La stå tomt for å beholde"
                      className="h-10"
                    />
                  </div>
                )}

                {/* SMS / Telefon - for customer users */}
                {editUserData.role === 'customer' && (
                  <div className="rounded-xl border border-slate-200 p-4 space-y-3">
                    <div className="text-xs font-semibold text-slate-700 uppercase tracking-wider">SMS-innstillinger</div>
                    <div>
                      <Label className="text-xs font-medium text-slate-500 mb-1.5 block">Telefonnummer</Label>
                      <div className="flex gap-2">
                        <Input
                          value={editUserData.phone_country_code}
                          onChange={(e) => setEditUserData({ ...editUserData, phone_country_code: e.target.value })}
                          className="w-20 h-10 text-center"
                          placeholder="+47"
                        />
                        <Input
                          value={editUserData.phone_number}
                          onChange={(e) => setEditUserData({ ...editUserData, phone_number: e.target.value.replace(/[^0-9\s]/g, '') })}
                          className="flex-1 h-10"
                          placeholder="123 45 678"
                        />
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-sky-50 rounded-lg border border-sky-100">
                      <Checkbox
                        id="sms_2fa"
                        checked={editUserData.sms_2fa_enabled}
                        onCheckedChange={(checked) => setEditUserData({ ...editUserData, sms_2fa_enabled: checked })}
                      />
                      <div>
                        <label htmlFor="sms_2fa" className="text-sm font-medium cursor-pointer text-sky-900">
                          SMS som OTP-metode
                        </label>
                        <p className="text-xs text-sky-600">Brukeren mottar engangskode på SMS ved innlogging</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 p-3 bg-teal-50 rounded-lg border border-teal-100">
                      <Checkbox
                        id="sms_notif"
                        checked={editUserData.sms_notifications_enabled}
                        onCheckedChange={(checked) => setEditUserData({ ...editUserData, sms_notifications_enabled: checked })}
                      />
                      <div>
                        <label htmlFor="sms_notif" className="text-sm font-medium cursor-pointer text-teal-900">
                          SMS-varsler
                        </label>
                        <p className="text-xs text-teal-600">Varsle bruker via SMS når nye prøver sendes</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Status toggles */}
                <div className="space-y-2">
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Checkbox
                      id="is_active"
                      checked={editUserData.is_active}
                      onCheckedChange={(checked) => setEditUserData({ ...editUserData, is_active: checked })}
                    />
                    <div>
                      <label htmlFor="is_active" className="text-sm font-medium cursor-pointer">Aktiv bruker</label>
                      <p className="text-xs text-slate-500">Deaktiverte brukere kan ikke logge inn</p>
                    </div>
                  </div>
                  {editUserData.auth_provider === 'local' && (
                    <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-100">
                      <Checkbox
                        id="skip_2fa"
                        checked={editUserData.skip_2fa}
                        onCheckedChange={(checked) => setEditUserData({ ...editUserData, skip_2fa: checked })}
                      />
                      <div>
                        <label htmlFor="skip_2fa" className="text-sm font-medium cursor-pointer text-amber-900">Hopp over 2FA</label>
                        <p className="text-xs text-amber-700">Bruker trenger ikke sette opp to-faktor</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setEditUserDialogOpen(false)}>Avbryt</Button>
                <Button onClick={saveUserEdit} className="bg-teal-500 hover:bg-teal-600">
                  <Check className="w-4 h-4 mr-2" />
                  Lagre endringer
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Summary */}
          <div className="mt-8 grid grid-cols-3 gap-4">
            {[
              { val: companies.length, label: 'Selskaper', color: 'text-teal-600 bg-teal-50 border-teal-100' },
              { val: customerUsers.length, label: 'Kundebrukarar', color: 'text-blue-600 bg-blue-50 border-blue-100' },
              { val: users.length, label: 'Totalt brukarar', color: 'text-slate-600 bg-slate-50 border-slate-100' },
            ].map((s, i) => (
              <div key={i} className={`rounded-xl border p-4 text-center ${s.color}`}>
                <div className="text-2xl font-bold">{s.val}</div>
                <div className="text-xs mt-0.5">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}